import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { ChevronRight, ChevronLeft, Diamond, ExternalLink } from 'lucide-react';

const API_URL = window.location.origin;

// Question definitions with logic
const QUESTIONS = {
  q1_leadership: {
    section: 'Leadership & Capacity',
    question: 'Which best describes your leadership responsibility and influence right now?',
    type: 'single',
    options: [
      { value: 'lead_teams', label: 'I lead teams, departments, or major initiatives' },
      { value: 'own_business', label: 'I own or operate a business with consistent revenue' },
      { value: 'oversee_strategy', label: 'I oversee budgets, strategy, or decision-making authority' },
      { value: 'transitioning', label: 'I am transitioning but have previously held significant leadership authority' },
      { value: 'other', label: 'Other' }
    ]
  },
  q2_income: {
    section: 'Leadership & Capacity',
    question: 'What is your current monthly income (personal or business revenue)?',
    type: 'single',
    options: [
      { value: 'under_8k', label: 'Under $8,000', redirect: 'inner_circle' },
      { value: '8k_15k', label: '$8,000\u2013$15,000' },
      { value: '15k_30k', label: '$15,000\u2013$30,000' },
      { value: '30k_plus', label: '$30,000+' }
    ]
  },
  q3_decision: {
    section: 'Leadership & Capacity',
    question: 'For strategic experiences or containers like Inner Edge and Collective, are you the primary decision-maker?',
    type: 'single',
    options: [
      { value: 'yes_primary', label: 'Yes, I make investment decisions' },
      { value: 'shared', label: 'I share decision-making with a partner' },
      { value: 'require_approval', label: 'I require approval from someone else' }
    ]
  },
  q4_season: {
    section: 'Emotional & Inner Readiness',
    question: 'Which speaks to your current season?',
    type: 'single',
    options: [
      { value: 'tired_unseen', label: "I've built success but feel emotionally tired or unseen" },
      { value: 'scale_without_losing', label: 'I want to scale without losing myself' },
      { value: 'strategy_activation', label: "I'm ready for strategy and activation" },
      { value: 'networking_tactical', label: 'I mostly want networking and tactical resources', redirect: 'inner_edge' }
    ]
  },
  q5_leaks: {
    section: 'Emotional & Inner Readiness',
    question: 'Where do you feel the biggest "pull" or leak in your life? (Select all that apply.)',
    type: 'multiple',
    options: [
      { value: 'boundaries', label: 'Boundaries and people-pleasing' },
      { value: 'health', label: 'Health & energy sustainability' },
      { value: 'isolation', label: 'Emotional isolation or loneliness' },
      { value: 'relational', label: 'Relational strain (marriage/partnership)' },
      { value: 'generational', label: 'Generational repair (adult children/family burden)' },
      { value: 'overachievement', label: 'Overachievement without fulfillment' },
      { value: 'something_off', label: "I'm not sure\u2026 but something feels off" },
      { value: 'thriving', label: "Nothing feels off \u2014 I'm thriving", followUp: true }
    ]
  },
  q5_followup: {
    section: 'Emotional & Inner Readiness',
    question: 'If everything feels aligned, what inspired you to apply now?',
    type: 'text',
    conditional: true
  },
  q6_pattern: {
    section: 'Coachability & Ownership',
    question: "When something in your life isn't working, your instinct is to:",
    type: 'single',
    options: [
      { value: 'push_harder', label: 'Push harder' },
      { value: 'fix_myself', label: 'Fix it myself' },
      { value: 'pray_wait', label: 'Pray and wait' },
      { value: 'talk_through', label: 'Talk it through with a trusted person' },
      { value: 'avoid', label: 'Avoid it' },
      { value: 'never_slowed', label: "I've never slowed down enough to see the pattern" }
    ]
  },
  q7_challenge: {
    section: 'Coachability & Ownership',
    question: 'The Sister Diamonds Collective includes compassionate but direct challenge that will help break old patterns. Are you open?',
    type: 'single',
    options: [
      { value: 'yes_truth', label: 'Yes \u2014 I want truth with love' },
      { value: 'yes_uncomfortable', label: "Yes \u2014 even if it's uncomfortable" },
      { value: 'unsure', label: "I'm unsure" },
      { value: 'encouragement_only', label: 'I prefer encouragement only', redirect: 'inner_circle' }
    ]
  },
  q8_investment: {
    section: 'Investment Readiness',
    question: 'The Sister Diamonds Collective ranges from $8,000\u2013$25,000 depending on access and structure. If aligned, are you prepared to invest within 30 days?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'yes_payments', label: 'Yes, with structured payments' },
      { value: 'explore_timing', label: 'I would need to explore timing' },
      { value: 'not_now', label: 'Not at this time', redirect: 'inner_circle' }
    ]
  },
  q9_cost: {
    section: 'Investment Readiness',
    question: 'If nothing changes in the next 12 months, what will it cost you?',
    type: 'text',
    placeholder: 'Share the emotional stakes, legacy cost, health cost, relational cost, peace cost...'
  },
  q10_why_now: {
    section: 'Investment Readiness',
    question: 'What has shifted internally that made you complete this application today?',
    type: 'text',
    placeholder: 'Share what has changed or awakened in you...'
  }
};

const QUESTION_ORDER = [
  'q1_leadership', 'q2_income', 'q3_decision',
  'q4_season', 'q5_leaks', 'q5_followup',
  'q6_pattern', 'q7_challenge',
  'q8_investment', 'q9_cost', 'q10_why_now'
];

const ENDINGS = {
  collective: {
    title: 'You May Be Aligned',
    subtitle: 'The Sister Diamonds Collective',
    body: 'Your responses indicate alignment with the Sister Diamonds Collective. The next step is a private alignment conversation.',
    cta: 'Schedule Your Interview',
    link: 'https://calendly.com/crystal-and-dr-nicole/one-on-one-with-crystal-and-dr-nicole',
    accent: 'terracotta'
  },
  inner_edge: {
    title: 'Your Next Powerful Step',
    subtitle: 'Inner Edge Activation',
    body: 'Your season is calling for strategy, momentum, and activation. Inner Edge Activation is a focused, high-impact experience designed to clarify direction, remove blocks, and fuel forward movement. Schedule a conversation to explore if Inner Edge is right for you.',
    cta: 'Schedule Your Conversation',
    link: 'https://calendly.com/crystal-and-dr-nicole/one-on-one-with-crystal-and-dr-nicole',
    accent: 'terracotta'
  },
  inner_circle: {
    title: 'Rest, Renewal & Foundational Growth',
    subtitle: 'The Inner Circle',
    body: 'This moment is an invitation to build foundation, strengthen boundaries, and deepen self-leadership. The Inner Circle offers monthly community, support, and growth at a sustainable pace.',
    cta: 'Join Inner Circle',
    link: 'https://events.sisterdiamonds.com/innercircle',
    accent: 'forest'
  },
  pathways: {
    title: 'Begin Where You Are',
    subtitle: 'Explore Pathways',
    body: 'Transformation unfolds in seasons. We invite you to begin with free resources \u2014 our podcast and Sister Diamonds App \u2014 for reflection, clarity, and weekly nourishment.',
    cta: 'Explore Pathways',
    link: 'https://www.sisterdiamonds.com/pathways',
    accent: 'forest'
  }
};

const CollectiveApplicationPage = () => {
  const navigate = useNavigate();
  const [started, setStarted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [redirect, setRedirect] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [contactInfo, setContactInfo] = useState({ name: '', email: '', phone: '' });
  const [showContactForm, setShowContactForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const getVisibleQuestions = () => {
    return QUESTION_ORDER.filter(qKey => {
      const q = QUESTIONS[qKey];
      if (q.conditional) {
        if (qKey === 'q5_followup') {
          const leaksAnswer = answers['q5_leaks'] || [];
          return leaksAnswer.includes('thriving');
        }
      }
      return true;
    });
  };

  const visibleQuestions = getVisibleQuestions();
  const currentQuestionKey = visibleQuestions[currentStep];
  const currentQuestion = QUESTIONS[currentQuestionKey];
  const progress = ((currentStep + 1) / visibleQuestions.length) * 100;

  const handleAnswer = (value) => {
    const q = currentQuestion;
    if (q.type === 'multiple') {
      const current = answers[currentQuestionKey] || [];
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      setAnswers({ ...answers, [currentQuestionKey]: updated });
    } else if (q.type === 'text') {
      setAnswers({ ...answers, [currentQuestionKey]: value });
    } else {
      setAnswers({ ...answers, [currentQuestionKey]: value });
      const selectedOption = q.options?.find(opt => opt.value === value);
      if (selectedOption?.redirect) {
        setRedirect(selectedOption.redirect);
      }
    }
  };

  const canProceed = () => {
    const answer = answers[currentQuestionKey];
    if (currentQuestion.type === 'multiple') return answer && answer.length > 0;
    if (currentQuestion.type === 'text') return answer && answer.trim().length > 10;
    return !!answer;
  };

  const handleNext = () => {
    if (currentStep < visibleQuestions.length - 1) {
      if (redirect) { setShowContactForm(true); return; }
      setCurrentStep(currentStep + 1);
    } else {
      determineOutcome();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) { setCurrentStep(currentStep - 1); setRedirect(null); }
  };

  const determineOutcome = () => {
    setRedirect('collective');
    setShowContactForm(true);
  };

  const handleSubmitApplication = async () => {
    if (!contactInfo.name || !contactInfo.email) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/collective/application`, {
        contact: contactInfo,
        answers: answers,
        outcome: redirect,
        completed_at: new Date().toISOString()
      });
      setShowContactForm(false);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting application:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Opening Screen ───
  if (!started) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4" data-testid="collective-start-screen">
        <div className="max-w-2xl w-full">
          <Card className="border-0 shadow-2xl overflow-hidden rounded-2xl">
            <div className="bg-forest p-10 md:p-14 text-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 20% 50%, #C06C54 0%, transparent 50%), radial-gradient(circle at 80% 50%, #E8DCCA 0%, transparent 50%)'}} />
              <div className="relative">
                <Diamond className="h-12 w-12 mx-auto mb-5" style={{color: '#C06C54'}} />
                <h1 className="font-cormorant text-4xl md:text-5xl font-bold tracking-tight mb-3" style={{color: '#F9F8F6'}}>
                  Sister Diamonds Collective
                </h1>
                <div className="w-16 h-0.5 bg-terracotta mx-auto mb-3" />
                <p className="font-manrope text-sm tracking-widest uppercase" style={{color: '#C06C54'}}>Application</p>
              </div>
            </div>

            <CardContent className="p-8 md:p-10 space-y-6 bg-white">
              <p className="font-manrope leading-relaxed text-base" style={{color: '#3D3D3D'}}>
                This is a curated, transformational container for women who are ready for alignment,
                soulful leadership evolution, and deep impact. We are intentional about who enters
                this Collective — not to exclude, but to protect depth, trust, and transformation.
              </p>

              <div className="border-l-4 border-terracotta p-5 rounded-r-lg" style={{backgroundColor: '#F9F8F6'}}>
                <p className="font-manrope font-medium text-sm leading-relaxed" style={{color: '#1A3C34'}}>
                  Answer honestly. This form uses alignment pathways to help you find the right
                  next container for you.
                </p>
                <p className="text-sm mt-2 italic font-manrope" style={{color: '#C06C54'}}>
                  Your season matters.
                </p>
              </div>

              <Button
                data-testid="start-application-btn"
                onClick={() => setStarted(true)}
                className="w-full py-6 text-base font-manrope tracking-wide rounded-xl transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5 shadow-md"
                style={{backgroundColor: '#C06C54', color: '#FFFFFF'}}
              >
                Start Application
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ─── Contact Info Collection ───
  if (showContactForm) {
    const ending = ENDINGS[redirect];
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4" data-testid="contact-form-screen">
        <Card className="max-w-xl w-full border-0 shadow-2xl rounded-2xl overflow-hidden">
          <div className="bg-forest p-6 text-center">
            <Diamond className="h-8 w-8 mx-auto mb-3" style={{color: '#C06C54'}} />
            <h2 className="font-cormorant text-2xl font-bold" style={{color: '#F9F8F6'}}>Almost There</h2>
            <div className="w-10 h-0.5 bg-terracotta mx-auto mt-2" />
          </div>
          <CardContent className="p-8 space-y-6 bg-white">
            <p className="text-center font-manrope text-sm" style={{color: '#3D3D3D'}}>
              Please share your contact information to complete your application.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-manrope font-semibold text-forest uppercase tracking-wider mb-1.5">Full Name *</label>
                <input
                  data-testid="contact-name-input"
                  type="text"
                  value={contactInfo.name}
                  onChange={(e) => setContactInfo({ ...contactInfo, name: e.target.value })}
                  className="w-full px-4 py-3 border border-sand rounded-xl focus:ring-2 focus:ring-forest/30 focus:border-forest font-manrope text-sm transition-all"
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label className="block text-xs font-manrope font-semibold text-forest uppercase tracking-wider mb-1.5">Email *</label>
                <input
                  data-testid="contact-email-input"
                  type="email"
                  value={contactInfo.email}
                  onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                  className="w-full px-4 py-3 border border-sand rounded-xl focus:ring-2 focus:ring-forest/30 focus:border-forest font-manrope text-sm transition-all"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block text-xs font-manrope font-semibold text-forest uppercase tracking-wider mb-1.5">Phone (optional)</label>
                <input
                  data-testid="contact-phone-input"
                  type="tel"
                  value={contactInfo.phone}
                  onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-sand rounded-xl focus:ring-2 focus:ring-forest/30 focus:border-forest font-manrope text-sm transition-all"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <Button
              data-testid="submit-application-btn"
              onClick={handleSubmitApplication}
              disabled={!contactInfo.name || !contactInfo.email || submitting}
              className="w-full bg-terracotta hover:bg-terracotta/90 text-white py-4 font-manrope tracking-wide rounded-xl transition-all duration-300"
            >
              {submitting ? 'Submitting...' : 'Complete Application'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Final Ending Screen ───
  if (redirect && submitted) {
    const ending = ENDINGS[redirect];
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4" data-testid="ending-screen">
        <Card className="max-w-xl w-full border-0 shadow-2xl overflow-hidden rounded-2xl">
          <div className="bg-forest p-10 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 30% 30%, #C06C54 0%, transparent 50%)'}} />
            <div className="relative">
              <Diamond className="h-10 w-10 mx-auto mb-4" style={{color: '#C06C54'}} />
              <h1 className="font-cormorant text-3xl md:text-4xl font-bold mb-2" style={{color: '#F9F8F6'}}>
                {ending.title}
              </h1>
              <div className="w-12 h-0.5 bg-terracotta mx-auto mt-2 mb-3" />
              <p className="font-manrope text-sm tracking-widest uppercase" style={{color: '#C06C54'}}>{ending.subtitle}</p>
            </div>
          </div>

          <CardContent className="p-8 md:p-10 space-y-6 text-center bg-white">
            <p className="leading-relaxed font-manrope text-base" style={{color: '#3D3D3D'}}>
              {ending.body}
            </p>

            <a
              data-testid="ending-cta-link"
              href={ending.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 w-full bg-terracotta hover:bg-terracotta/90 text-white py-4 px-6 rounded-xl font-manrope font-semibold tracking-wide transition-all duration-300"
            >
              {ending.cta}
              <ExternalLink className="h-4 w-4" />
            </a>

            <p className="text-xs text-gray-400 font-manrope tracking-wide">
              Thank you for your honesty and openness. Your season matters.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Question Screen ───
  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4" data-testid="question-screen">
      <Card className="max-w-2xl w-full border-0 shadow-2xl rounded-2xl overflow-hidden">
        <CardContent className="p-6 md:p-8 bg-white">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs font-manrope font-semibold text-terracotta uppercase tracking-widest">
                {currentQuestion.section}
              </span>
              <span className="text-xs font-manrope text-gray-400 tabular-nums">
                {currentStep + 1} / {visibleQuestions.length}
              </span>
            </div>
            <div className="h-1 bg-sand/50 rounded-full overflow-hidden">
              <div
                className="h-full bg-forest rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Question */}
          <div className="mb-8">
            <h2 className="font-cormorant text-2xl md:text-3xl font-bold text-forest mb-6 leading-tight">
              {currentQuestion.question}
            </h2>

            {/* Single Choice */}
            {currentQuestion.type === 'single' && (
              <div className="space-y-3">
                {currentQuestion.options.map((option) => {
                  const isSelected = answers[currentQuestionKey] === option.value;
                  return (
                    <button
                      key={option.value}
                      data-testid={`option-${option.value}`}
                      onClick={() => handleAnswer(option.value)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                        isSelected
                          ? 'border-forest bg-forest/5'
                          : 'border-sand/60 hover:border-forest/40 hover:bg-cream'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                          isSelected ? 'border-forest' : 'border-gray-300'
                        }`}>
                          {isSelected && <div className="w-3 h-3 rounded-full bg-forest" />}
                        </div>
                        <span className="font-manrope text-sm text-gray-700">{option.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Multiple Choice */}
            {currentQuestion.type === 'multiple' && (
              <div className="space-y-3">
                {currentQuestion.options.map((option) => {
                  const selected = (answers[currentQuestionKey] || []).includes(option.value);
                  return (
                    <button
                      key={option.value}
                      data-testid={`option-${option.value}`}
                      onClick={() => handleAnswer(option.value)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                        selected
                          ? 'border-forest bg-forest/5'
                          : 'border-sand/60 hover:border-forest/40 hover:bg-cream'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                          selected ? 'border-forest bg-forest' : 'border-gray-300'
                        }`}>
                          {selected && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className="font-manrope text-sm text-gray-700">{option.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Text Input */}
            {currentQuestion.type === 'text' && (
              <Textarea
                data-testid="text-answer-input"
                value={answers[currentQuestionKey] || ''}
                onChange={(e) => handleAnswer(e.target.value)}
                placeholder={currentQuestion.placeholder || 'Share your thoughts...'}
                className="min-h-[150px] text-sm font-manrope border-sand/60 rounded-xl focus:ring-2 focus:ring-forest/30 focus:border-forest"
              />
            )}
          </div>

          {/* Navigation */}
          <div className="flex justify-between gap-4">
            <Button
              data-testid="back-btn"
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 0}
              className="flex-1 border-sand text-forest hover:bg-cream font-manrope rounded-xl"
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back
            </Button>

            <Button
              data-testid="next-btn"
              onClick={handleNext}
              disabled={!canProceed()}
              className="flex-1 font-manrope tracking-wide rounded-xl transition-all duration-300 hover:opacity-90 text-white"
              style={{backgroundColor: '#C06C54'}}
            >
              {currentStep === visibleQuestions.length - 1 ? 'Complete' : 'Continue'}
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CollectiveApplicationPage;
